import { useCallback, useState, useEffect } from 'react';
import styles from './ScenarioEditableImage.module.css';
import { pencilIcon } from '../../assets/icons';
import { fallbackImage } from '../../assets/images';

interface ScenarioEditableImageProps {
  icon?: string | null;
  altText: string;
  onUpdate: (file: File) => Promise<void>;
}

function ScenarioEditableImage({ icon, altText, onUpdate }: ScenarioEditableImageProps): JSX.Element {
  // Add a timestamp state to use for cache busting
  const [timestamp, setTimestamp] = useState<number>(Date.now());
  // Add loading state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Update timestamp when icon changes
  useEffect(() => {
    setTimestamp(Date.now());
  }, [icon]);
  
  const handleIconClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          setIsLoading(true);
          await onUpdate(file);
          // Update timestamp after upload to force image refresh
          setTimestamp(Date.now());
        } catch (error) {
          console.error('Error uploading image:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    input.click();
  }, [onUpdate]);

  // Add cache-busting parameter to the image URL
  const imageUrl = icon ? `${icon}?t=${timestamp}` : fallbackImage;

  return (
    <div className={styles.imageContainer}>
      <div className={styles.imageWrapper}>
        <img 
          src={imageUrl} 
          alt={altText} 
          className={styles.scenarioFullImage}
        />
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner}></div>
            <div className={styles.loadingText}>Uploading...</div>
          </div>
        )}
        <div className={styles.buttonOverlay}>
          <div className={styles.imageEditSection}>
            <div className={styles.imageFieldLabel}>Icon</div>
          </div>
          <div 
            className={`${styles.uploadIconSection} ${isLoading ? styles.disabled : ''}`} 
            onClick={isLoading ? undefined : handleIconClick}
          >
            <img src={pencilIcon} alt="Edit Icon" className={styles.pencilIcon} />
            {isLoading ? 'Uploading...' : 'Upload Icon'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScenarioEditableImage; 